// Core imports
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, Link, createSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import Highlighter from 'react-highlight-words';
import moment from 'moment-timezone';

// Component imports
import { Table, Space, Tooltip, Row, Col, Input, Popconfirm, Spin, Tag, Form, Button, Typography } from 'antd';
import { SearchOutlined, HighlightOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import BotAvatar from '../UI/BotAvatar';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters, useObservatorySearchParams, useObservatoryTenantsFilterState } from '../UI/ObservatoryFiltersRow';
import SideModal from '../Layout/SideModal';
import AutomatedAgentTestsResultsView from '../UI/AutomatedAgentTestsResultsView';

// Misc imports
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { AUTOMATED_TESTS_PERCENT_FILTER, COLOR_STATUS_TYPES, DEFAULT_DATETIME_FORMAT, RESULT_THRESHOLDS } from '../../constants/main';
import { useFetchAutomatedTestResultsQuery, useDeleteAutomatedTestResultMutation, useFetchAutomatedTestsQuery } from '../../store/automatedTests/automatedTestsApi';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_ORGANIZATION_TESTING } from '../../constants/navigation';

const PAGE_SIZE = 10;

const AutomatedAgentTestsResultsTable = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [filtersForm] = Form.useForm();

  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || PAGE_SIZE, 10);
  const resultIdParam = searchParams.get('resultId');

  const { data: organization, isLoading: isLoadingOrganization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId });

  const { data: automatedTestsData } = useFetchAutomatedTestsQuery({
    orgId,
    urlParams: {
      page: 0,
      pageSize: 10000,
      sortBy: 'name',
      order: 'ASC'
    }
  }, { skip: !orgId });

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const observatorySearchParams = useObservatorySearchParams(orgTimezone);
  const observatoryFilters = useObservatoryFilters(orgTimezone);

  const observatoryFiltersKey = useMemo(() => JSON.stringify(observatoryFilters || []), [observatoryFilters]);
  const prevObservatoryFiltersKeyRef = useRef(observatoryFiltersKey);
  const initialTenantFilters = useInitialTenantFilters();
  const { shouldSkipTenantsFilter } = useObservatoryTenantsFilterState(observatorySearchParams);

  const [deleteAutomatedTestResult] = useDeleteAutomatedTestResultMutation();

  const { data: testResults, isFetching: isFetchingResults } = useFetchAutomatedTestResultsQuery({
    orgId,
    urlParams: {
      page: currentPage > 0 ? currentPage - 1 : currentPage,
      pageSize,
      sortBy: searchParams.get('sortBy') || 'runTime',
      order: searchParams.get('order') || 'desc',
      tenantsFilter: observatorySearchParams.tenantsFilter,
      ...(observatorySearchParams.startDate && { startDate: observatorySearchParams.startDate }),
      ...(observatorySearchParams.endDate && { endDate: observatorySearchParams.endDate }),
      ...(searchParams.get('automatedTestId') && { automatedTestId: searchParams.get('automatedTestId') }),
      ...(searchParams.get('search') && { searchText: searchParams.get('search') }),
      ...(searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.GTE) && { percentGte: searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.GTE) }),
      ...(searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.LTE) && { percentLte: searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.LTE) })
    }
  }, { skip: !orgId || shouldSkipTenantsFilter });

  const activeResult = useMemo(() => {
    if (!resultIdParam || !testResults?.items) {
      return null;
    }
    return testResults.items.find((item) => item.id === resultIdParam) || null;
  }, [resultIdParam, testResults?.items]);

  useEffect(() => {
    if (prevObservatoryFiltersKeyRef.current !== observatoryFiltersKey) {
      prevObservatoryFiltersKeyRef.current = observatoryFiltersKey;
      handleTableChange({ shouldResetPagination: true });
    }
  }, [observatoryFiltersKey]);

  const activeResultFilter = useMemo(() => {
    const percentGte = searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.GTE);
    const percentLte = searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.LTE);

    if (percentGte === `${AUTOMATED_TESTS_PERCENT_FILTER.UPPER_THRESHOLD}` && !percentLte) {
      return RESULT_THRESHOLDS.PASSED;
    }
    if (percentGte === `${AUTOMATED_TESTS_PERCENT_FILTER.LOWER_THRESHOLD}` && percentLte === `${AUTOMATED_TESTS_PERCENT_FILTER.UPPER_THRESHOLD - 1}`) {
      return RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT;
    }
    if (!percentGte && percentLte === `${AUTOMATED_TESTS_PERCENT_FILTER.LOWER_THRESHOLD - 1}`) {
      return RESULT_THRESHOLDS.FAILED;
    }

    return null;
  }, [searchParams]);

  const handleOpenSideModal = (record) => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.set('resultId', record.id);
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const handleCloseSideModal = () => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.delete('resultId');
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const handleDeleteResult = async (id) => {
    if (resultIdParam === id) {
      handleCloseSideModal();
    }
    await deleteAutomatedTestResult({ orgId, id }).unwrap();
  };

  const debouncedUpdateUrl = useDebouncedCallback((value) => {
    const urlSearchParams = new URLSearchParams(searchParams);
    if (value) {
      urlSearchParams.set('search', value);
    } else {
      urlSearchParams.delete('search');
    }
    urlSearchParams.set('page', '1');
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  }, 300);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedUpdateUrl(value);
  };

  const handleTableChange = (
    {
      pagination = { current: Number(searchParams.get('page')) || 1, pageSize: Number(searchParams.get('pageSize')) || pageSize },
      sorter = { field: searchParams.get('sortBy') || 'runTime', order: searchParams.get('order') || 'desc' },
      filters = {},
      shouldResetPagination = false
    } = {}
  ) => {
    const urlSearchParams = new URLSearchParams(searchParams);

    urlSearchParams.set('page', shouldResetPagination ? 1 : pagination.current);
    urlSearchParams.set('pageSize', pagination.pageSize);

    if (sorter.field && sorter.order) {
      urlSearchParams.set('sortBy', sorter.field);
      urlSearchParams.set('order', sorter.order === 'ascend' ? 'asc' : 'desc');
    } else {
      urlSearchParams.set('sortBy', 'runTime');
      urlSearchParams.set('order', 'desc');
    }

    if (filters.automatedTestName && filters.automatedTestName.length > 0) {
      urlSearchParams.set('automatedTestId', filters.automatedTestName[0]);
    } else {
      urlSearchParams.delete('automatedTestId');
    }

    urlSearchParams.delete(AUTOMATED_TESTS_PERCENT_FILTER.GTE);
    urlSearchParams.delete(AUTOMATED_TESTS_PERCENT_FILTER.LTE);

    if (filters.successRate && filters.successRate.length > 0) {
      const filterValue = filters.successRate[0];
      switch (filterValue) {
        case RESULT_THRESHOLDS.PASSED:
          urlSearchParams.set(AUTOMATED_TESTS_PERCENT_FILTER.GTE, AUTOMATED_TESTS_PERCENT_FILTER.UPPER_THRESHOLD);
          break;
        case RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT:
          urlSearchParams.set(AUTOMATED_TESTS_PERCENT_FILTER.GTE, AUTOMATED_TESTS_PERCENT_FILTER.LOWER_THRESHOLD);
          urlSearchParams.set(AUTOMATED_TESTS_PERCENT_FILTER.LTE, AUTOMATED_TESTS_PERCENT_FILTER.UPPER_THRESHOLD - 1);
          break;
        case RESULT_THRESHOLDS.FAILED:
          urlSearchParams.set(AUTOMATED_TESTS_PERCENT_FILTER.LTE, AUTOMATED_TESTS_PERCENT_FILTER.LOWER_THRESHOLD - 1);
          break;
        default:
          break;
      }
    }

    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const sortingInfo = useMemo(() => {
    const order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    const columnKey = searchParams.get('sortBy') || 'runTime';
    return { order, columnKey };
  }, [searchParams]);

  const columns = [
    {
      title: t('Result (Last Run)'),
      dataIndex: 'runTime',
      key: 'runTime',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'runTime' && sortingInfo.order,
      render: (date) => (date ? moment(date).tz(organization?.timezone).format(DEFAULT_DATETIME_FORMAT) : '-')
    },
    {
      title: t('Agent'),
      dataIndex: 'examinedTenantId',
      key: 'examinedTenantId',
      render: (tenantId) => {
        const bot = bots?.items?.find(b => b.id === tenantId);
        return bot ? <BotAvatar avatarSize={24} bot={bot} /> : tenantId;
      }
    },
    {
      title: t('Test Name'),
      dataIndex: 'automatedTestName',
      key: 'automatedTestName',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'automatedTestName' && sortingInfo.order,
      filters: automatedTestsData?.items?.map(test => ({ text: test.name, value: test.id })) || [],
      filteredValue: searchParams.get('automatedTestId') ? [searchParams.get('automatedTestId')] : null,
      filterMultiple: false,
      filterSearch: true,
      render: (text, record) => {
        const currentSearch = (searchParams.get('search') || '').toLowerCase();

        const tooltipContent = (
          <div className="p-1">
            <Typography.Text strong className="d-block mb-2">
              {t('Test Details')}
            </Typography.Text>
            <ul style={{ listStyleType: 'disc', paddingLeft: '15px', margin: 0 }}>
              <li>{record.automatedTestName || 'N/A'}</li>
              <li>{record.resultDetails?.testDescription || 'N/A'}</li>
              <li>{record.resultDetails?.languageCode || 'N/A'}</li>
              <li>{record.resultDetails?.examinedTenantName || 'N/A'}</li>
              <li>{record.resultDetails?.flowName || 'N/A'}</li>
              <li>{record.runTime ? moment(record.runTime).tz(organization?.timezone).format(DEFAULT_DATETIME_FORMAT) : 'N/A'}</li>
            </ul>
          </div>
        );
        return (
          <Space onClick={(e) => e.stopPropagation()}>
            <Link
              to={`${replaceUrlParams(NAV_ORGANIZATION_TESTING, { orgId })}?${createSearchParams({ ...observatorySearchParams, testId: record.automatedTestId }).toString()}`}
              replace
            >
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={[searchTerm]}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
              />
            </Link>
            <Tooltip title={tooltipContent} overlayStyle={{ maxWidth: '400px' }} showArrow={false} overlayClassName="bg-layout-gray">
              <InfoCircleOutlined className="text-secondary" />
            </Tooltip>
            {currentSearch && !record.automatedTestName?.toLowerCase().includes(currentSearch) && (
              <Tooltip overlayClassName="pointer-events-none" title={t('Match found in run contents')}>
                <HighlightOutlined className="text-primary" />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: t('Sessions Passed'),
      dataIndex: 'successRate',
      key: 'successRate',
      align: 'center',
      width: 250,
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'successRate' && sortingInfo.order,
      filteredValue: activeResultFilter ? [activeResultFilter] : null,
      filterMultiple: false,
      filters: [
        { text: t('Passed (>=80%)'), value: RESULT_THRESHOLDS.PASSED },
        { text: t('Passed (70-79%)'), value: RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT },
        { text: t('Failed (<70%)'), value: RESULT_THRESHOLDS.FAILED }
      ],
      render: (rate) => {
        let color = rate >= 80 ? COLOR_STATUS_TYPES.SUCCESS : (rate >= 70 ? COLOR_STATUS_TYPES.WARNING : COLOR_STATUS_TYPES.ERROR);
        const formattedRate = rate % 1 === 0 ? rate : rate.toFixed(2);
        return <Tag className="fw-600 py-1 px-3" color={color}>{`${formattedRate}%`}</Tag>;
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 100,
      align: 'center',
      render: (record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            title={t('Delete this test result?')}
            description={t('This action cannot be undone.')}
            onConfirm={() => handleDeleteResult(record.id)}
            okText={t('Delete')}
            cancelText={t('Cancel')}
            placement="topRight"
          >
            <Tooltip title={t('Delete Result')}>
              <Button shape="circle" type="text" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const isLoading = isLoadingOrganization || isFetchingResults || isFetchingBots;

  if (isLoadingOrganization || !organization) {
    return (
      <Row className="d-flex justify-content-center align-items-center">
        <Col>
          <Spin />
        </Col>
      </Row>
    );
  }

  return (
    <div className="w-100">
      <Form
        layout="inline"
        name="resultsFiltersForm"
        form={filtersForm}
        initialValues={{
          tenantsFilter: initialTenantFilters,
          rangePicker: [observatoryFilters.startDate, observatoryFilters.endDate]
        }}>
        <div className="w-100">
          <ObservatoryFiltersRow
            form={filtersForm}
            rangePickerProps={{
              disabled: false,
              timezone: orgTimezone
            }}
            agentsPickerProps={{
              showDeployments: true,
              agentsList: bots?.items || [],
              disabled: isLoading
            }}
          />
        </div>
        <Row gutter={24} className="mb-2 w-100" justify="space-between">
          <Col flex="auto">
            <Space className="flex-wrap">
              <Input
                size="large"
                placeholder={t('Search in contents...')}
                prefix={<SearchOutlined className="text-secondary" />}
                allowClear
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Space>
          </Col>
        </Row>
      </Form>
      <Row gutter={24}>
        <Col xs={24}>
          <Table
            dataSource={testResults?.items}
            columns={columns}
            scroll={{ y: 680 }}
            className="scroll-table row-cursor-pointer"
            rowClassName={(record) => {
              return record.id === activeResult?.id ? 'cursor-pointer bg-table-row-selected' : 'cursor-pointer';
            }}
            rowKey="id"
            loading={isLoading}
            onRow={(record) => ({
              onClick: () => handleOpenSideModal(record)
            })}
            onChange={(pagination, filters, sorter) => handleTableChange({ pagination, filters, sorter })}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: testResults?.total || 0,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Results')}: ${total}`
            }}
          />
        </Col>
      </Row>

      <SideModal
        open={!!resultIdParam}
        onClose={handleCloseSideModal}
        title={activeResult ? `${t('Run Details')}: ${activeResult?.automatedTestName}` : t('Run Details')}
        bodyClassName="p-0"
        destroyOnClose
        width={900}
        hasFullScreenButton
      >
        <div className="px-6 pt-6 flex-grow-1 h-100 overflow-y-auto">
          {resultIdParam && (
            <AutomatedAgentTestsResultsView resultId={resultIdParam} />
          )}
        </div>
      </SideModal>

    </div>
  );
};

export default AutomatedAgentTestsResultsTable;
